import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2, X, User, Save, Phone, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import CpfCnpjInput from '../components/CpfCnpjInput';
import CatalogItemModal from '../components/CatalogItemModal';
import { mensagemCpfCnpj } from '../utils/validators';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmt2 = (v) => Number(v || 0).toFixed(2).replace('.', ',');

const STATUS_OPTIONS = [
  { value: 'nao_enviado', label: 'Não enviado' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'pago', label: 'Pago' },
];

const TIPO_OPTIONS = [
  { value: 'orcamento', label: 'Orçamento' },
  { value: 'proposta', label: 'Proposta Técnica' },
  { value: 'contrato', label: 'Contrato' },
];

const PAGAMENTO_TIPOS = [
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_avista', label: 'Cartão à Vista' },
  { value: 'cartao_parcelado', label: 'Cartão Parcelado' },
  { value: 'boleto', label: 'Boleto' },
];

const emptyServiceItem = (pos) => ({ position: pos, descricao: '', quantidade: '', unidade: 'SV', valor_unitario: '', valor_total: 0, tipo: 'avulso', secao: 'servico', ref_id: null });
const emptyMaterialItem = (pos) => ({ position: pos, descricao: '', quantidade: '', unidade: 'UN', valor_unitario: '', valor_total: 0, tipo: 'avulso', secao: 'material', ref_id: null });
const isService = (i) => i.secao === 'servico' || i.tipo === 'servico';
const emptyClient = { nome: '', cpf_cnpj: '', tel: '', whatsapp: '', email: '', end: '', num: '', cep: '', cidade: '', uf: '' };
const emptyPagamento = { tipo: 'pix', parcelas: 1, vencimento_boleto: '' };

function getEmployeeSession() {
  try { return JSON.parse(localStorage.getItem('orcfacil_employee') || 'null'); } catch { return null; }
}

export default function EmployeeProposalForm() {
  const navigate = useNavigate();
  const { companyId, proposalId } = useParams();
  const isEdit = !!proposalId;
  const session = getEmployeeSession();

  const empApi = axios.create({
    baseURL: `${BACKEND}/api`,
    headers: { Authorization: `Bearer ${session?.access_token}` }
  });

  const [form, setForm] = useState({
    titulo: '', tipo: 'orcamento', status: 'nao_enviado',
    emissao: new Date().toISOString().split('T')[0], validade: '',
    cliente_id: '', cliente_nome: '', cliente_dados: {}, obs: '', desconto: 0, itens: [], num: '',
    prazo_execucao: '',
  });
  const [pagamento, setPagamento] = useState(emptyPagamento);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [newClient, setNewClient] = useState(emptyClient);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [itemSearch, setItemSearch] = useState({});
  const [showItemSearch, setShowItemSearch] = useState({});
  const [loading, setLoading] = useState(false);
  const [nextPos, setNextPos] = useState(1);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [catalogModal, setCatalogModal] = useState({ open: false, pos: null, kind: 'produto', secao: 'material', descricao: '' });

  // Dirty tracking
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const dirtyReady = useRef(false);
  useEffect(() => { const t = setTimeout(() => { dirtyReady.current = true; }, 500); return () => clearTimeout(t); }, []);
  useEffect(() => { if (dirtyReady.current) setIsDirty(true); }, [form, pagamento]);
  useEffect(() => {
    const fn = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [isDirty]);
  const safeNavigate = (path) => {
    if (isDirty) { setPendingNavPath(path); setShowUnsavedDialog(true); }
    else navigate(path);
  };
  const confirmLeave = () => { setIsDirty(false); setShowUnsavedDialog(false); navigate(pendingNavPath || `/portal/${companyId}`); };

  useEffect(() => {
    if (!session || session.employee?.company_id !== companyId) {
      navigate(`/portal/${companyId}`);
      return;
    }
    loadMasterData();
    loadCompanyInfo();
    if (isEdit) loadProposal();
  }, [proposalId]);

  const loadCompanyInfo = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/employees/portal/${companyId}/info`);
      setCompanyInfo(r.data);
    } catch {}
  };

  const loadMasterData = async () => {
    try {
      const [catalog, cls] = await Promise.all([
        empApi.get('/employees/portal/catalog'),
        empApi.get('/employees/portal/company-clients'),
      ]);
      setProducts(catalog.data.products || []);
      setServices(catalog.data.services || []);
      setClients(cls.data || []);
    } catch { toast.error('Erro ao carregar dados'); }
  };

  const loadProposal = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/employees/portal/${companyId}/proposal/${proposalId}`);
      const p = r.data;
      setForm({
        titulo: p.titulo, tipo: p.tipo, status: p.status,
        emissao: p.emissao || '', validade: p.validade || '',
        cliente_id: p.cliente_id || '', cliente_nome: p.cliente_nome || '',
        cliente_dados: p.cliente_dados || {}, obs: p.obs || '',
        desconto: p.desconto || 0, itens: p.itens || [], num: p.num || '',
        prazo_execucao: p.prazo_execucao || '',
      });
      if (p.forma_pagamento) setPagamento({ tipo: p.forma_pagamento.tipo || 'pix', parcelas: p.forma_pagamento.parcelas || 1, vencimento_boleto: p.forma_pagamento.vencimento_boleto || '' });
      setClientSearch(p.cliente_nome || '');
      const maxPos = Math.max(0, ...(p.itens || []).map(i => i.position));
      setNextPos(maxPos + 1);
      const seedSearch = {};
      (p.itens || []).forEach(i => { if (i.descricao?.trim() && !i.ref_id) seedSearch[i.position] = i.descricao; });
      setItemSearch(seedSearch);
    } catch { toast.error('Erro ao carregar orçamento'); navigate(`/portal/${companyId}`); }
  };

  const totalServicos = form.itens.filter(isService).reduce((s, i) => s + (i.valor_total || 0), 0);
  const totalMateriais = form.itens.filter(i => !isService(i)).reduce((s, i) => s + (i.valor_total || 0), 0);
  const subtotal = totalServicos + totalMateriais;
  const total = Math.max(0, subtotal - form.desconto);
  const valorParcela = pagamento.tipo === 'cartao_parcelado' && pagamento.parcelas > 0 ? total / pagamento.parcelas : 0;

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addServiceItem = () => {
    const pos = nextPos; setNextPos(p => p + 1);
    setForm(p => ({ ...p, itens: [...p.itens, emptyServiceItem(pos)] }));
  };
  const addMaterialItem = () => {
    const pos = nextPos; setNextPos(p => p + 1);
    setForm(p => ({ ...p, itens: [...p.itens, emptyMaterialItem(pos)] }));
  };

  const updateItem = (pos, field, value) => {
    setForm(p => ({
      ...p,
      itens: p.itens.map(i => {
        if (i.position !== pos) return i;
        const u = { ...i, [field]: value };
        if (field === 'quantidade' || field === 'valor_unitario') {
          u.valor_total = (Number(u.quantidade) || 0) * (Number(u.valor_unitario) || 0);
        }
        if (field === 'descricao') {
          u.ref_id = null;
          u.tipo = 'avulso';
        }
        return u;
      })
    }));
  };

  const removeItem = (pos) => setForm(p => ({ ...p, itens: p.itens.filter(i => i.position !== pos) }));

  const selectCatalogItem = (pos, item, type, secao) => {
    setForm(p => ({
      ...p,
      itens: p.itens.map(i => {
        if (i.position !== pos) return i;
        const qty = i.quantidade;
        const price = item.valor || 0;
        return { ...i, descricao: (item.descricao || '').toUpperCase(), valor_unitario: price, valor_total: (Number(qty) || 0) * price, unidade: item.unidade || (secao === 'servico' ? 'SV' : 'UN'), tipo: type, secao, ref_id: item.id };
      })
    }));
    setShowItemSearch(p => ({ ...p, [pos]: false }));
    setItemSearch(p => ({ ...p, [pos]: '' }));
  };

  const getServiceResults = (pos) => {
    const q = (itemSearch[pos] || '').toLowerCase();
    if (!q) return [];
    return services.filter(s => s.descricao?.toLowerCase().includes(q)).slice(0, 8);
  };

  const getMaterialResults = (pos) => {
    const q = (itemSearch[pos] || '').toLowerCase();
    if (!q) return [];
    return products.filter(p => p.descricao?.toLowerCase().includes(q)).slice(0, 8);
  };

  const openCatalogModal = (pos, secao) => {
    const kind = secao === 'servico' ? 'servico' : 'produto';
    setCatalogModal({ open: true, pos, kind, secao, descricao: itemSearch[pos] || '' });
  };

  const saveCatalogItem = async (payload) => {
    const endpoint = catalogModal.kind === 'servico' ? '/employees/portal/catalog/service' : '/employees/portal/catalog/product';
    const res = await empApi.post(endpoint, payload);
    return res.data;
  };

  const handleCatalogSaved = (item) => {
    const { pos, kind, secao } = catalogModal;
    if (kind === 'servico') setServices(p => [...p, item]);
    else setProducts(p => [...p, item]);
    selectCatalogItem(pos, item, kind, secao);
  };

  const filteredClients = clients.filter(c =>
    c.nome?.toLowerCase().includes(clientSearch.toLowerCase()) || c.cpf_cnpj?.includes(clientSearch)
  ).slice(0, 8);

  const selectClient = (c) => {
    setClientSearch(c.nome);
    setForm(p => ({ ...p, cliente_id: c.id, cliente_nome: c.nome, cliente_dados: c }));
    setShowClientSuggestions(false); setShowNewClientForm(false);
  };

  const handleClientSearch = (val) => {
    const upVal = val.toUpperCase();
    setClientSearch(upVal);
    setForm(p => ({ ...p, cliente_nome: upVal, cliente_id: '', cliente_dados: {} }));
    setShowClientSuggestions(true);
    const exact = clients.find(c => c.nome.toLowerCase() === upVal.toLowerCase());
    if (!exact && upVal.length > 0) setShowNewClientForm(true);
    else setShowNewClientForm(false);
  };

  const saveNewClient = async () => {
    if (!clientSearch && !newClient.nome) return;
    if (newClient.cpf_cnpj) {
      const erro = mensagemCpfCnpj(newClient.cpf_cnpj);
      if (erro) { toast.error(erro); return; }
    }
    try {
      const res = await empApi.post('/employees/portal/company-clients', { ...newClient, nome: clientSearch || newClient.nome });
      const c = res.data;
      setClients(p => [...p, c]);
      selectClient(c);
      setNewClient(emptyClient);
      setShowNewClientForm(false);
      toast.success('Cliente cadastrado!');
    } catch { toast.error('Erro ao cadastrar cliente'); }
  };

  const handleSave = async () => {
    if (!form.titulo) { toast.error('Informe o título do orçamento'); return; }
    const semCadastro = form.itens.find(i => i.descricao?.trim() && !i.ref_id);
    if (semCadastro) {
      toast.error(`"${semCadastro.descricao}" precisa ser selecionado ou cadastrado no catálogo antes de salvar`);
      return;
    }
    setLoading(true);
    try {
      const fp = {
        tipo: pagamento.tipo,
        parcelas: (pagamento.tipo === 'cartao_parcelado' || pagamento.tipo === 'boleto') ? Number(pagamento.parcelas) : null,
        valor_parcela: (pagamento.tipo === 'cartao_parcelado' || pagamento.tipo === 'boleto') && pagamento.parcelas > 0 ? Number((total / pagamento.parcelas).toFixed(2)) : null,
        vencimento_boleto: pagamento.tipo === 'boleto' ? pagamento.vencimento_boleto : null,
      };
      const itens = form.itens.map(i => ({ ...i, quantidade: Number(i.quantidade) || 0, valor_unitario: Number(i.valor_unitario) || 0, valor_total: Number(i.valor_total) || 0 }));

      if (isEdit) {
        await empApi.put(`/employees/portal/proposals/${proposalId}/full`, { ...form, forma_pagamento: fp, itens });
        toast.success('Orçamento atualizado!');
        setIsDirty(false);
        navigate(`/portal/${companyId}`);
      } else {
        await empApi.post('/employees/portal/proposals/create', {
          ...form, forma_pagamento: fp, itens,
          employee_id: session.employee.id,
          company_id: companyId,
        });
        toast.success('Orçamento criado!');
        setIsDirty(false);
        navigate(`/portal/${companyId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setLoading(false); }
  };

  const sortedServices = [...form.itens].filter(isService).sort((a, b) => a.position - b.position);
  const sortedMaterials = [...form.itens].filter(i => !isService(i)).sort((a, b) => a.position - b.position);

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" theme="dark" richColors />

      {/* Header */}
      <header className="bg-zinc-900/95 border-b border-zinc-800 px-4 py-3 flex items-center gap-3 sticky top-0 z-40">
        <button onClick={() => safeNavigate(`/portal/${companyId}`)} className="text-zinc-400 hover:text-white p-1.5 transition-colors">
          <ArrowLeft size={18} />
        </button>
        {companyInfo?.logo
          ? <img src={companyInfo.logo} alt="Logo" className="h-7 w-auto object-contain" />
          : <span className="font-heading font-bold text-white text-sm">{companyInfo?.company_nome || 'Portal'}</span>
        }
        <span className="text-xs text-zinc-500 flex-1">{isEdit ? `Editando #${form.num}` : 'Novo Orçamento'}</span>
        <button data-testid="emp-save-proposal-btn" onClick={handleSave} disabled={loading}
          className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded-xl transition-colors text-sm">
          <Save size={14} />
          {loading ? 'Salvando...' : 'Salvar'}
        </button>
      </header>

      <div className="max-w-4xl mx-auto space-y-5 p-4 pb-16">
        {/* Basic Info */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-white text-sm">Informações gerais</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="field-label">Título / Descrição *</label>
              <input data-testid="emp-titulo-input" required className="field-input" placeholder="Ex: Instalação de CFTV" value={form.titulo} onChange={e => setField('titulo', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label className="field-label">Tipo</label>
              <select className="field-select" value={form.tipo} onChange={e => setField('tipo', e.target.value)}>
                {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Status</label>
              <select className="field-select" value={form.status} onChange={e => setField('status', e.target.value)}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Data de emissão</label>
              <input type="date" className="field-input" value={form.emissao} onChange={e => setField('emissao', e.target.value)} />
            </div>
            <div>
              <label className="field-label">Validade</label>
              <input type="date" className="field-input" value={form.validade} onChange={e => setField('validade', e.target.value)} />
            </div>
            <div>
              <label className="field-label">Prazo de execução (dias)</label>
              <input type="number" min="0" inputMode="numeric" className="field-input" placeholder="Ex: 5" value={form.prazo_execucao} onChange={e => setField('prazo_execucao', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Client */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-white text-sm">Cliente</h2>
          <div className="relative">
            <label className="field-label">Nome do cliente</label>
            <div className="relative">
              <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="emp-client-input" className="field-input pl-9" placeholder="Digite ou selecione..."
                value={clientSearch}
                onChange={e => handleClientSearch(e.target.value)}
                onFocus={() => setShowClientSuggestions(true)}
                onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
              />
            </div>
            {showClientSuggestions && filteredClients.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 overflow-hidden shadow-xl max-h-52 overflow-y-auto">
                {filteredClients.map(c => (
                  <button key={c.id} onClick={() => selectClient(c)} className="w-full text-left px-4 py-3 hover:bg-zinc-700 transition-colors border-b border-zinc-700/50 last:border-0">
                    <div className="text-sm font-medium text-white">{c.nome}</div>
                    {c.cpf_cnpj && <div className="text-xs text-zinc-500">{c.cpf_cnpj}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {showNewClientForm && clientSearch && (
            <div className="bg-zinc-800/50 border border-dashed border-orange-500/30 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-orange-400 font-medium">Cliente não encontrado — cadastrar novo:</p>
                <button onClick={() => setShowNewClientForm(false)} className="text-zinc-500 hover:text-zinc-300"><X size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="field-label">Nome *</label>
                  <input className="field-input" value={clientSearch} onChange={e => { setClientSearch(e.target.value.toUpperCase()); setNewClient(p => ({ ...p, nome: e.target.value.toUpperCase() })); }} />
                </div>
                <div>
                  <label className="field-label">Telefone</label>
                  <input type="tel" inputMode="tel" className="field-input" placeholder="(99) 99999-9999" value={newClient.tel} onChange={e => setNewClient(p => ({ ...p, tel: e.target.value }))} />
                </div>
                <div>
                  <label className="field-label">WhatsApp</label>
                  <input type="tel" inputMode="tel" className="field-input" placeholder="(99) 99999-9999" value={newClient.whatsapp} onChange={e => setNewClient(p => ({ ...p, whatsapp: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className="field-label">CPF/CNPJ</label>
                  <CpfCnpjInput value={newClient.cpf_cnpj} onChange={(v) => setNewClient(p => ({ ...p, cpf_cnpj: v }))} onlyMode="cpf_cnpj" />
                </div>
              </div>
              <div className="flex justify-center">
                <button onClick={saveNewClient}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors">
                  <User size={15} /> Salvar cliente
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── SERVIÇO ──────────────────────────────────────────────── */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-white text-sm">Serviço</h2>
            <button onClick={addServiceItem} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold px-3 py-1.5 rounded-xl transition-colors">
              <Plus size={14} /> Adicionar serviço
            </button>
          </div>
          <div className="space-y-4">
            {sortedServices.length === 0 && <div className="text-center py-4 text-zinc-600 text-sm">Nenhum serviço adicionado</div>}
            {sortedServices.map(item => {
              const results = getServiceResults(item.position);
              return (
                <div key={item.position} className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-blue-400 uppercase tracking-wide">Serviço #{String(item.position).padStart(2, '0')}</span>
                    <button onClick={() => removeItem(item.position)} className="text-zinc-600 hover:text-red-400 transition-colors p-1"><Trash2 size={14} /></button>
                  </div>
                  <div className="relative">
                    <input className="field-input text-sm" placeholder="Buscar ou digitar serviço..."
                      value={item.descricao}
                      onChange={e => { const v = e.target.value.toUpperCase(); updateItem(item.position, 'descricao', v); setItemSearch(p => ({ ...p, [item.position]: v })); setShowItemSearch(p => ({ ...p, [item.position]: true })); }}
                      onFocus={() => setShowItemSearch(p => ({ ...p, [item.position]: true }))}
                      onBlur={() => setTimeout(() => setShowItemSearch(p => ({ ...p, [item.position]: false })), 200)}
                    />
                    {showItemSearch[item.position] && results.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 shadow-xl max-h-52 overflow-y-auto">
                        {results.map(r => (
                          <button key={r.id} onClick={() => selectCatalogItem(item.position, r, 'servico', 'servico')} className="w-full text-left px-3 py-2.5 hover:bg-zinc-700 border-b border-zinc-700/50 last:border-0">
                            <div className="flex items-center justify-between"><span className="text-xs font-medium text-white">{r.descricao}</span><span className="text-xs text-zinc-400">{brl(r.valor)}</span></div>
                          </button>
                        ))}
                      </div>
                    )}
                    {itemSearch[item.position] && results.length === 0 && !item.ref_id && (
                      <div className="flex items-center justify-between gap-2 mt-1.5 text-xs">
                        <span className="text-amber-400">Serviço inexistente</span>
                        <button type="button" onClick={() => openCatalogModal(item.position, 'servico')}
                          className="flex items-center gap-1 text-orange-400 hover:text-orange-300 font-medium">
                          <Plus size={12} /> Cadastrar
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">QTD</label>
                      <input type="text" inputMode="decimal" className="field-input text-sm text-right" placeholder="0" value={item.quantidade || ''} onChange={e => updateItem(item.position, 'quantidade', e.target.value)} /></div>
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">UNIDADE</label>
                      <input className="field-input text-sm text-center" value={item.unidade} onChange={e => updateItem(item.position, 'unidade', e.target.value.toUpperCase())} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">VALOR UNIT</label>
                      <input type="number" min="0" step="0.01" inputMode="decimal" className="field-input text-sm text-right" placeholder="0,00" value={item.valor_unitario} onChange={e => updateItem(item.position, 'valor_unitario', e.target.value)} /></div>
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">TOTAL</label>
                      <div className="field-input text-sm text-right bg-zinc-700/30 text-orange-400 font-mono font-medium">{fmt2(item.valor_total)}</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── MATERIAIS ──────────────────────────────────────────────── */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-white text-sm">Itens do orçamento</h2>
            <button onClick={addMaterialItem} className="flex items-center gap-2 border border-orange-500/40 hover:bg-orange-500/10 text-orange-400 text-sm px-3 py-1.5 rounded-lg transition-colors">
              <Plus size={14} /> Adicionar item
            </button>
          </div>
          <div className="space-y-4">
            {sortedMaterials.length === 0 && <div className="text-center py-4 text-zinc-600 text-sm">Nenhum material adicionado</div>}
            {sortedMaterials.map(item => {
              const results = getMaterialResults(item.position);
              return (
                <div key={item.position} className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-green-400 uppercase tracking-wide">Item #{String(item.position).padStart(2, '0')}</span>
                    <button onClick={() => removeItem(item.position)} className="text-zinc-600 hover:text-red-400 transition-colors p-1"><Trash2 size={14} /></button>
                  </div>
                  <div className="relative">
                    <input className="field-input text-sm" placeholder="Buscar ou digitar material..."
                      value={item.descricao}
                      onChange={e => { const v = e.target.value.toUpperCase(); updateItem(item.position, 'descricao', v); setItemSearch(p => ({ ...p, [item.position]: v })); setShowItemSearch(p => ({ ...p, [item.position]: true })); }}
                      onFocus={() => setShowItemSearch(p => ({ ...p, [item.position]: true }))}
                      onBlur={() => setTimeout(() => setShowItemSearch(p => ({ ...p, [item.position]: false })), 200)}
                    />
                    {showItemSearch[item.position] && results.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-30 bg-zinc-800 border border-zinc-700 rounded-xl mt-1 shadow-xl max-h-52 overflow-y-auto">
                        {results.map(r => (
                          <button key={r.id} onClick={() => selectCatalogItem(item.position, r, 'produto', 'material')} className="w-full text-left px-3 py-2.5 hover:bg-zinc-700 border-b border-zinc-700/50 last:border-0">
                            <div className="flex items-center justify-between"><span className="text-xs font-medium text-white">{r.descricao}</span><span className="text-xs text-zinc-400">{brl(r.valor)}</span></div>
                          </button>
                        ))}
                      </div>
                    )}
                    {itemSearch[item.position] && results.length === 0 && !item.ref_id && (
                      <div className="flex items-center justify-between gap-2 mt-1.5 text-xs">
                        <span className="text-amber-400">Produto inexistente</span>
                        <button type="button" onClick={() => openCatalogModal(item.position, 'material')}
                          className="flex items-center gap-1 text-orange-400 hover:text-orange-300 font-medium">
                          <Plus size={12} /> Cadastrar
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">QTD</label>
                      <input type="text" inputMode="decimal" className="field-input text-sm text-right" placeholder="0" value={item.quantidade || ''} onChange={e => updateItem(item.position, 'quantidade', e.target.value)} /></div>
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">UNIDADE</label>
                      <input className="field-input text-sm text-center" value={item.unidade} onChange={e => updateItem(item.position, 'unidade', e.target.value.toUpperCase())} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">VALOR UNIT</label>
                      <input type="number" min="0" step="0.01" inputMode="decimal" className="field-input text-sm text-right" placeholder="0,00" value={item.valor_unitario} onChange={e => updateItem(item.position, 'valor_unitario', e.target.value)} /></div>
                    <div><label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">TOTAL</label>
                      <div className="field-input text-sm text-right bg-zinc-700/30 text-orange-400 font-mono font-medium">{fmt2(item.valor_total)}</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── TOTAIS ──────────────────────────────────────────────── */}
        {form.itens.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-2">
            <h2 className="font-heading font-semibold text-white text-sm mb-3">Resumo de valores</h2>
            {totalServicos > 0 && <div className="flex justify-between text-sm text-zinc-400"><span>Valor total dos serviços</span><span className="font-mono">{brl(totalServicos)}</span></div>}
            {totalMateriais > 0 && <div className="flex justify-between text-sm text-zinc-400"><span>Valor total dos materiais</span><span className="font-mono">{brl(totalMateriais)}</span></div>}
            {(totalServicos > 0 || totalMateriais > 0) && <div className="border-t border-zinc-800 pt-2 flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span className="font-mono">{brl(subtotal)}</span></div>}
            <div className="flex items-center justify-between text-sm text-zinc-400">
              <span>Desconto</span>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500">R$</span>
                <input type="number" min="0" step="0.01" className="w-28 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-right text-sm text-white focus:outline-none focus:border-orange-500"
                  value={form.desconto} onChange={e => setField('desconto', Number(e.target.value))} />
              </div>
            </div>
            <div className="flex justify-between text-base font-bold text-white border-t border-zinc-800 pt-2 mt-2">
              <span>Total</span><span className="font-mono text-orange-400">{brl(total)}</span>
            </div>
          </div>
        )}

        {/* Payment */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <h2 className="font-heading font-semibold text-white text-sm">Forma de pagamento</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PAGAMENTO_TIPOS.map(pt => (
              <button key={pt.value} onClick={() => setPagamento(p => ({ ...p, tipo: pt.value }))}
                className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-all ${pagamento.tipo === pt.value ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}>
                {pt.label}
              </button>
            ))}
          </div>
          {pagamento.tipo === 'cartao_parcelado' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Nº de parcelas</label>
                <input type="number" min="2" max="48" className="field-input" value={pagamento.parcelas} onChange={e => setPagamento(p => ({ ...p, parcelas: Number(e.target.value) }))} />
              </div>
              <div>
                <label className="field-label">Valor por parcela</label>
                <div className="field-input bg-zinc-800/50 text-right font-mono text-orange-400">{pagamento.parcelas > 0 ? brl(valorParcela) : '—'}</div>
              </div>
            </div>
          )}
          {pagamento.tipo === 'boleto' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Nº de parcelas (1 a 5)</label>
                  <select className="field-select" value={pagamento.parcelas} onChange={e => setPagamento(p => ({ ...p, parcelas: Number(e.target.value) }))}>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}x</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Data de vencimento</label>
                  <input type="date" className="field-input" value={pagamento.vencimento_boleto} onChange={e => setPagamento(p => ({ ...p, vencimento_boleto: e.target.value }))} />
                </div>
              </div>
              {pagamento.parcelas > 1 && (
                <div className="bg-zinc-800/50 rounded-xl p-3 text-sm text-zinc-400">
                  <span className="font-medium text-white">{pagamento.parcelas}x</span> de <span className="text-orange-400 font-mono font-medium">{brl(total / pagamento.parcelas)}</span> · Total: <span className="font-mono">{brl(total)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Observations */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
          <h2 className="font-heading font-semibold text-white text-sm">Observações</h2>
          <textarea className="field-input min-h-[80px] resize-y" rows={3} placeholder="Condições, prazo de entrega, garantias..." value={form.obs} onChange={e => setField('obs', e.target.value.toUpperCase())} />
        </div>

        {/* Save button at bottom */}
        <div className="flex gap-3">
          <button onClick={() => safeNavigate(`/portal/${companyId}`)} className="flex-1 py-3 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors">
            Cancelar
          </button>
          <button data-testid="emp-save-bottom-btn" onClick={handleSave} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors">
            <Save size={16} />
            {loading ? 'Salvando...' : 'Salvar Orçamento'}
          </button>
        </div>
      </div>

      <CatalogItemModal
        open={catalogModal.open}
        kind={catalogModal.kind}
        editing={null}
        initialDescricao={catalogModal.descricao}
        onSave={saveCatalogItem}
        onClose={() => setCatalogModal(p => ({ ...p, open: false }))}
        onSaved={handleCatalogSaved}
      />

      {/* Unsaved changes dialog */}
      {showUnsavedDialog && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-2xl">
            <h3 className="text-white font-semibold text-lg">Sair sem salvar?</h3>
            <p className="text-zinc-400 text-sm">Você tem alterações não salvas. Se sair agora, perderá todas as mudanças.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowUnsavedDialog(false)} className="flex-1 border border-zinc-700 text-zinc-300 py-2.5 rounded-xl text-sm hover:bg-zinc-800 transition-colors">
                Continuar editando
              </button>
              <button onClick={confirmLeave} className="flex-1 bg-red-500 hover:bg-red-400 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors">
                Sair sem salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
